import PropTypes from 'prop-types';
import React, {
  useCallback, useState, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col,
  Drawer,
  Form,
  Row,
  Select,
} from 'antd';
import { getContactAddressBooks } from 'ontraccr-common/lib/Contacts';

import DrawerSubmitFooter from '../common/containers/DrawerSubmitFooter';
import {
  searchBoards,
} from './state/search.actions';
import QuerySelector from './QuerySelector';
import BoardsSelector from './BoardsSelector';
import { getSearchDrawerState, validateNewValue } from './searchHelpers';
import { boardNativeOptions, filterOptions } from './search.constants';
import CustomFilterRuleInput from './CustomFilterRuleInput';
import ConditionalRenderingRow from '../forms/FormBuilder/FormFields/ConditionalRenderingRow';
import { getIdMap } from '../helpers/helpers';
import useProjectCostcodesFetch from '../costcodes/useProjectCostcodesFetch';
import useProjectOrGlobalCostcodes from '../costcodes/useProjectOrGlobalCostcodes';

export default function BoardsSearchDrawer({
  visible,
  onClose,
  conditionsOnly = false,
  placement = 'right',
  mask = true,
  width = 1100,
  title = 'Search Boards',
  initialQuery,
  onSearch = null,
  submitTitle = 'Submit',
}) {
  const dispatch = useDispatch();

  const [selectedQuery, setSelectedQuery] = useState();
  const [ourQuery, setOurQuery] = useState({});
  const [filterType, setFilterType] = useState('and');
  const [customFilterRule, setCustomFilterRule] = useState(null);

  const { boardIds, cardTypeId } = ourQuery;

  const searchQueryMap = useSelector((state) => state.search.boardQueries);
  const reduxSelectedQuery = useSelector((state) => state.search.selectedQuery);
  const reduxCurrentQuery = useSelector((state) => state.search.currentQuery);
  const customers = useSelector((state) => state.customers.customers);
  const projects = useSelector((state) => state.projects.projects);
  const users = useSelector((state) => state.users.users);
  const phases = useSelector((state) => state.costcodes.phases);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const equipment = useSelector((state) => state.equipment.equipment);
  const vendors = useSelector((state) => state.vendors.vendors);
  const formTemplates = useSelector((state) => state.forms.templates);
  const labels = useSelector((state) => state.labels);
  const buckets = useSelector((state) => state.buckets.buckets);
  const scheduleCategories = useSelector((state) => state.schedule.categories);
  const globalAddressBooks = useSelector((state) => state.contacts.globalAddressBooks);
  const formStatuses = useSelector((state) => state.forms.statuses);
  const boardCardTemplates = useSelector((state) => state.boards.cardTemplates);
  const boards = useSelector((state) => state.boards.boards);
  const boardStatuses = useSelector((state) => state.boards.boardStatuses);
  const boardColors = useSelector((state) => state.boards.boardColors);
  const materials = useSelector((state) => state.materials.materials);

  const materialsList = useMemo(() => Object.values(materials ?? {}), [materials]);
  const customerList = useMemo(() => Object.values(customers), [customers]);
  const vendorList = useMemo(() => Object.values(vendors), [vendors]);
  const formTemplateList = useMemo(() => Object.values(formTemplates), [formTemplates]);
  const formStatusList = useMemo(() => Object.values(formStatuses), [formStatuses]);
  const boardCards = useMemo(() => Object.values(boardCardTemplates), [boardCardTemplates]);
  const boardList = useMemo(() => Object.values(boards), [boards]);
  const cardTypeList = useMemo(() => (
    boardCards.map((card) => ({
      label: card.title,
      value: card.id,
    }))
  ), [boardCards]);
  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);

  const contactAddressBooks = useMemo(() => (
    getContactAddressBooks(globalAddressBooks)
  ), [globalAddressBooks]);

  const costcodeProjectIds = useMemo(() => (
    visible ? projects.map(({ id }) => id) : []
  ), [visible, projects]);
  const divisionIds = useMemo(() => Array.from(selectedDivisions ?? []), [selectedDivisions]);

  const { projectCostcodes } = useProjectCostcodesFetch({ projectIds: costcodeProjectIds });
  const { costcodes: globalCostcodes } = useProjectOrGlobalCostcodes({
    projectId: null,
    divisionIds,
    visible,
  });

  const costcodes = useMemo(() => (
    [...projectCostcodes, ...globalCostcodes]
  ), [projectCostcodes, globalCostcodes]);

  const onSelectQuery = useCallback((id, newQuery) => {
    if (id === selectedQuery?.id) return;
    const q = newQuery || searchQueryMap[id];
    setSelectedQuery(q);
    setOurQuery(q?.query || {});
    setFilterType(q?.filterType || 'and');
    setCustomFilterRule(q?.customFilterRule || null);
  }, [searchQueryMap, selectedQuery]);

  const onFormulaChange = useCallback((newFormulas) => {
    setOurQuery({
      ...ourQuery,
      formulas: newFormulas,
    });
  }, [ourQuery]);

  const onBoardIdsChanged = useCallback((newBoardIds) => {
    let boardIdSet = new Set(newBoardIds || []);
    if (!boardIdSet.size && cardTypeId) {
      boardIdSet = new Set(
        boardList.filter((board) => board.cardTypeId === cardTypeId).map((board) => board.id),
      );
    }

    const validColorValues = new Set(
      boardColors
        .filter((entry) => boardIdSet.has(entry.boardId) && entry.label && entry.color)
        .map((entry) => `${entry.label}::${entry.color}`),
    );

    const formulas = (ourQuery?.formulas || []).reduce((acc, formula) => {
      if (formula.field !== 'color') {
        acc.push(formula);
        return acc;
      }
      if (!validColorValues.size) return acc;
      if (formula.operator?.includes('has')) {
        acc.push(formula);
        return acc;
      }

      const values = Array.isArray(formula.value) ? formula.value : [];
      const filtered = values.filter((value) => validColorValues.has(value));
      acc.push({
        ...formula,
        value: filtered.length ? filtered : null,
      });
      return acc;
    }, []);

    setOurQuery({
      ...ourQuery,
      boardIds: newBoardIds,
      formulas,
    });
  }, [ourQuery, boardColors, boardList, cardTypeId]);

  const onCardTypeIdChanged = useCallback((newCardTypeId) => {
    setOurQuery({
      ...ourQuery,
      cardTypeId: newCardTypeId,
      boardIds: [],
      formulas: ourQuery?.formulas?.filter((formula) => (
        !formula.field?.startsWith?.('field-')
        && formula.field !== 'color'
      )) || [],
    });
  }, [ourQuery]);

  const analyticsFieldIds = useMemo(() => (
    (ourQuery?.formulas ?? [])
      .filter((f) => f.analytics)
      .map((f) => f.field)
  ), [ourQuery?.formulas]);

  const onSubmit = useCallback(() => {
    if (onSearch) {
      onSearch(ourQuery);
      return;
    }
    const prom = dispatch(searchBoards({
      query: ourQuery,
      selectedQuery,
      filterType,
      analyticsFieldIds,
    }));
    onClose(prom);
  }, [onClose, ourQuery, selectedQuery, filterType, analyticsFieldIds, onSearch]);

  useEffect(() => {
    if (!visible) return;

    if (conditionsOnly) {
      setSelectedQuery(undefined);
      setOurQuery(initialQuery || {});
      setFilterType('and');
      setCustomFilterRule(null);
      return;
    }

    const drawerState = getSearchDrawerState('boards', reduxCurrentQuery, reduxSelectedQuery);
    if (!drawerState) {
      setSelectedQuery(undefined);
      setOurQuery({});
      setFilterType('and');
      setCustomFilterRule(null);
      return;
    }

    setOurQuery(drawerState.ourQuery);
    setFilterType(drawerState.filterType);
    setCustomFilterRule(drawerState.customFilterRule);
    setSelectedQuery(drawerState.selectedQuery);
  }, [visible, reduxCurrentQuery, reduxSelectedQuery, conditionsOnly, initialQuery]);

  const FormulaWrapper = useCallback(({ index, children }) => (
    <div style={{ marginBottom: 10, border: '1px solid #ccc', padding: 10 }}>
      <b>{index + 1}</b>
      {children}
    </div>
  ), []);

  const canSubmit = useMemo(() => {
    if (conditionsOnly) {
      return (ourQuery?.formulas?.length ?? 0) > 0 && !!cardTypeId;
    }
    if (filterType === 'custom') {
      return validateNewValue(customFilterRule, ourQuery?.formulas).isValid;
    }
    return (
      !!filterType && ourQuery?.formulas?.length > 0
      && cardTypeId
    );
  }, [filterType, ourQuery, customFilterRule, cardTypeId, boardIds, conditionsOnly]);

  const ourCardType = useMemo(() => (
    boardCardTemplates[cardTypeId]
  ), [cardTypeId, boardCardTemplates]);

  const relevantBoardStatuses = useMemo(() => {
    if (!cardTypeId) return [];

    let boardIdSet = new Set(boardIds);

    // If no boards are selected, use all boards of the selected card type
    if (!boardIdSet.size) {
      boardIdSet = new Set(
        boardList.filter((board) => board.cardTypeId === cardTypeId).map((board) => board.id),
      );
    }
    const statuses = boardStatuses.filter((status) => boardIdSet.has(status.boardId));
    return statuses.map((status) => {
      const board = boards[status.boardId];
      const prefix = board ? `${board.title} - ` : '';
      return {
        label: `${prefix}${status.title}`,
        value: `${status.boardId}.${status.id}`,
      };
    });
  }, [boardStatuses, boardIds, boardList, cardTypeId, boards]);

  const relevantColorLegendOptions = useMemo(() => {
    if (!cardTypeId) return [];

    let boardIdSet = new Set(boardIds);

    if (!boardIdSet.size) {
      boardIdSet = new Set(
        boardList.filter((board) => board.cardTypeId === cardTypeId).map((board) => board.id),
      );
    }

    // Same label+hex across boards collapse. Same label, different hex stays unique.
    const uniqueLegendEntries = new Map();
    boardColors
      .filter((entry) => boardIdSet.has(entry.boardId) && entry.label && entry.color)
      .forEach((entry) => {
        const key = `${entry.label}::${entry.color}`;
        if (!uniqueLegendEntries.has(key)) {
          uniqueLegendEntries.set(key, {
            label: entry.label,
            color: entry.color,
            value: key,
          });
        }
      });

    return Array.from(uniqueLegendEntries.values());
  }, [boardColors, boardIds, boardList, cardTypeId]);

  const additionalFields = useMemo(() => {
    if (!relevantColorLegendOptions.length) return boardNativeOptions;

    return [
      ...boardNativeOptions,
      {
        name: 'Color Legend Label',
        id: 'color',
        selectedType: 'dropdown',
        configProps: {
          dataType: 'BoardColorLegend',
        },
      },
    ];
  }, [relevantColorLegendOptions]);

  return (
    <Drawer
      title={title}
      visible={visible}
      onClose={onClose}
      width={width}
      placement={placement}
      mask={mask}
    >
      {!conditionsOnly && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: 'calc(100% - 950px)',
          }}
        >
          <QuerySelector
            searchQueryMap={searchQueryMap}
            currentQuery={ourQuery}
            selectedQuery={selectedQuery?.id}
            onSelectQuery={onSelectQuery}
            type="boards"
            filterType={filterType}
            customFilterRule={customFilterRule}
          />
        </div>
      )}
      <Col
        style={{
          height: 'calc(100% - 30px)',
          width: '100%',
          overflowY: 'scroll',
          overflowX: 'hidden',
          paddingRight: '10px',
        }}
      >
        {!conditionsOnly && (
          <>
            <Row>
              <span>Select Card Types:</span>
            </Row>
            <Select
              options={cardTypeList}
              placeholder="Select a card type"
              onChange={onCardTypeIdChanged}
              value={cardTypeId}
              style={{ width: '100%', margin: '10px 0px' }}
            />
            { !!cardTypeId && (
              <BoardsSelector
                value={boardIds}
                onChange={onBoardIdsChanged}
                cardTypeId={cardTypeId}
              />
            )}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
            }}
            >
              <span>Include data if it matches:</span>
              <Select
                options={filterOptions}
                placeholder="Select a filter type"
                onChange={setFilterType}
                value={filterType}
              />
            </div>
            {filterType === 'custom' && (
              <Form.Item
                label="Custom filter rules"
                name="custom"
              >
                <CustomFilterRuleInput
                  ruleValue={customFilterRule}
                  onChange={(newValue) => {
                    setCustomFilterRule(newValue);
                  }}
                  formulas={ourQuery?.formulas}
                />
              </Form.Item>
            )}
          </>
        )}
        <ConditionalRenderingRow
          onFormulaChange={onFormulaChange}
          conditionalRenderingFormula={ourQuery?.formulas}
          sections={ourCardType?.fields}
          id="search"
          customers={customerList}
          projects={projects}
          users={users}
          costcodes={costcodes}
          phases={phases}
          projectIdMap={projectIdMap}
          vendors={vendorList}
          equipment={equipment}
          formTemplates={formTemplateList}
          labels={labels}
          contactAddressBooks={contactAddressBooks}
          buckets={buckets}
          materials={materialsList}
          scheduleCategories={scheduleCategories}
          showType={false}
          showCheckbox={false}
          formStatuses={formStatusList}
          additionalFields={additionalFields}
          hasConditionalRendering
          FormulaWrapperComponent={FormulaWrapper}
          isAdvancedSearch
          boardStatuses={relevantBoardStatuses}
          boardColorLegend={relevantColorLegendOptions}
        />
      </Col>
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
        canSubmit={canSubmit}
        submitTitle={submitTitle}
      />
    </Drawer>
  );
}

BoardsSearchDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  conditionsOnly: PropTypes.bool,
  placement: PropTypes.string,
  mask: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.string,
  initialQuery: PropTypes.shape({
    boardIds: PropTypes.arrayOf(PropTypes.string),
    cardTypeId: PropTypes.string,
    formulas: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  onSearch: PropTypes.func,
  submitTitle: PropTypes.string,
};

BoardsSearchDrawer.defaultProps = {
  visible: false,
  conditionsOnly: false,
  placement: 'right',
  mask: true,
  width: 1100,
  title: 'Search Boards',
  onSearch: null,
  submitTitle: 'Submit',
};
